import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Alert, Form, Typography, Space, Checkbox } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined, GiftOutlined } from '@ant-design/icons';
import { useAuth } from './AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

const RegisterDialog = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 从 URL 参数中获取推荐码
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const refCode = searchParams.get('ref');
    if (refCode) {
      form.setFieldsValue({ referralCode: refCode });
    }
  }, [location.search, form]);

  const handleSubmit = async (values) => {
    setError('');
    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = values;
      await register(registerData);
      onClose();
      navigate('/notice');
    } catch (err) {
      setError(err.response?.data?.message || '註冊失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setError('');
    setLoading(false);
    if (onClose) onClose();
  };

  const validatePassword = ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value || getFieldValue('password') === value) {
        return Promise.resolve();
      }
      return Promise.reject(new Error('兩次輸入的密碼不一致'));
    },
  });

  return (
    <Modal
      title="註冊新賬號"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      width={600}
    >
      <div style={{ padding: '20px 0' }}>
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
          size="large"
          initialValues={{ wantCourse: true }}
        >
          <Form.Item
            label="姓名"
            name="name"
            rules={[
              { required: true, message: '請輸入姓名' },
              { min: 2, message: '姓名至少為2個字符' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="請輸入您的姓名"
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            label="電子信箱"
            name="email"
            rules={[
              { required: true, message: '請輸入電子信箱' },
              { type: 'email', message: '請輸入有效的電子信箱' }
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="請輸入您的電子信箱"
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            label="密碼"
            name="password"
            rules={[
              { required: true, message: '請輸入密碼' },
              { min: 6, message: '密碼長度至少為6位' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="請輸入您的密碼"
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            label="確認密碼"
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: '請確認密碼' },
              validatePassword
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="請再次輸入密碼"
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            label="推薦碼"
            name="referralCode"
            rules={[
              { required: true, message: '請輸入推薦碼' }
            ]}
          >
            <Input
              prefix={<GiftOutlined />}
              placeholder="請輸入推薦碼"
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            name="wantCourse"
            valuePropName="checked"
            style={{ marginBottom: 16 }}
          >
            <Checkbox disabled={loading}>
              我要索取動能課程
            </Checkbox>
          </Form.Item>

          <div style={{ marginBottom: 16 }}>
            <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>
              建議使用非通用推薦碼，未來可獲得更多優惠 ( 通用推薦碼：GUEST168 )
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
              註冊後請查收驗證郵件以完成註冊流程
            </Typography.Text>
          </div>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button onClick={handleClose} disabled={loading}>
                取消
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                註冊
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default RegisterDialog; 